import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import Search from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { styled } from '@mui/material/styles';
import {
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  tableCellClasses
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import Loader from '../../../common-components/loader';
import { SensorData } from '../../../Model/sensorData';
import { SensorDataMapper } from './sensorDataMapper';
import './sensorsTesting.scss';
import SearchSensors from '../../../Components/Sensors/SearchSensors';
import CustomAlert, { CustomAlertType } from '../../../Components/CustomAlert';
import constants from '../../../constants';
import authAxios from '../../../authorization/axios';

const SensorsTesting = () => {
  const { t } = useTranslation();
  const params: any = useParams();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [sensors, setSensors] = useState<any[]>([]);
  const [pagedSensors, setPagedSensors] = useState<any[]>([]);
  const [type, setType] = useState<'airqino' | 'turtle'>(params?.type);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tableOptions, setTableOptions] = useState<{
    record4page: number;
    count: number;
    page: number;
  }>({ record4page: 20, count: 0, page: 0 });
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [from, setFrom] = useState<number>(0);
  const [to, setTo] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const updateDatatable = (pageValue: number) => {
    const startIndex = pageValue * tableOptions.record4page;
    const endIndex = (pageValue + 1) * tableOptions.record4page;
    const pagedSensorsValue = sensors.slice(startIndex, endIndex);
    console.log(pagedSensorsValue);
    setPagedSensors(pagedSensorsValue);
    setTableOptions({
      ...tableOptions,
      count: sensors.length,
      page: pageValue
    });
  };

  useEffect(() => {
    if (params.type !== 'airqino') {
      setSensors([]);
      setType('turtle');
      updateDatatable(0);
    }
    if (params.type !== 'turtle') {
      setSensors([]);
      setType('airqino');
      updateDatatable(0);
    }
  }, [params.type, type]);

  useEffect(() => {
    updateDatatable(0);
  }, [sensors]);

  const getSensors = async (
    sensorsIds?: string[]
  ) => {
    const data: {
      sensorsIds?: string;
      from?: number;
      to?: number;
      include?: 'history' | 'last_data';
    } = {};
    if ((from || 0) > 0) {
      data.from = from;
    }
    if ((to || 0) > 0) {
      data.to = to;
    }

    if (sensorsIds) {
      data.sensorsIds = sensorsIds.join(',');
    }
    const res = await authAxios.get(
      constants.api.getSensorsTestingData.replace('{type}', type), { params: data });

    const sensorsByRange: { success: boolean; data: any[] } = res.data;
    return sensorsByRange;
  };

  const searchBySensorId = async (sensorId: string) => {
    const res = await authAxios.get(
      constants.api.getSensorTestingHistory
        .replace('{type}', type)
        .replace('{id}', sensorId)
    );
    const sensorById: { success: boolean; data: any[] } = res.data;
    return sensorById;
  };


  const showSensorsHistory = async (sensorId: string) => {
    setLoading(true);
    try {
      const result = await searchBySensorId(sensorId);
      setShowHistory(true);
      setHistory(result.data);
      setLoading(false);
      return sensorId;
    } catch (error) {
      setLoading(false);
    }
  };

  const renderTable = (
    sensorsData: SensorData[],
    onlyInfo: boolean = false
  ) => {
    const tableColumns = SensorDataMapper.getTableColumns(type);

    const trs = [];
    for (let i = 0; i < sensorsData.length; i += 1) {
      const sensorValue = sensorsData[i];

      const tr = (
        <>
          <tr id={`sensor-${sensorValue.sensorId}`}>
            <td>{sensorValue.sensorId}</td>
            <td>
              {DateTime.fromJSDate(new Date(sensorValue.date)).toFormat(
                'dd/MM/yyyy HH:mm:ss'
              )}
            </td>
            {onlyInfo && (
              <>
                {tableColumns.map((tc, index) => (
                  <td key={`c-${index}`}>{tc.value(sensorValue)}</td>
                ))}
              </>
            )}

            {!onlyInfo && (
              <td className="showHistory" style={{ display: 'flex' }}>
                <div onClick={() => showSensorsHistory(sensorValue.sensorId)}>
                  {t('VIEW')} <Search></Search>
                </div>
              </td>
            )}
          </tr>
        </>
      );
      trs.push(tr);
    }
    if (trs.length === 0) {
      const noResults = (
        <tr>
          <td className="no-result" colSpan={tableColumns.length + 1}>
            {t('NO_RESULTS')}
          </td>
        </tr>
      );
      trs.push(noResults);
    }
    return (
      <table id="sensors-table">
        <thead>
          <tr>
            <th>{t('SENSOR_ID')}</th>
            <th>{t('LAST_DATA_SENT')}</th>
            {onlyInfo && (
              <>
                {tableColumns.map((tc, j) => (
                  <th key={`h-${j}`}>{t(tc.label)}</th>
                ))}
              </>
            )}
            {!onlyInfo && <th>{t('ACTIONS')}</th>}
          </tr>
        </thead>
        <tbody>{trs}</tbody>
      </table>
    );
  };

  const pages = Math.ceil(tableOptions.count / tableOptions.record4page) - 1;
  console.log('pages', pages, tableOptions);

  const handleClose = () => {
    setShowHistory(false);
  };

  function Row(props: { row: any }) {
    const { row } = props;

    return (
      <React.Fragment>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell  align="left" component="th" scope="row">
            {row.sensorId}
          </TableCell>
          <TableCell align="left">
            {DateTime.fromJSDate(new Date(row.date)).toFormat(
              'dd/MM/yyyy HH:mm:ss'
            )}
          </TableCell>
          <TableCell align="left">
            <div onClick={() => showSensorsHistory(row.sensorId)}>
              {t('VIEW')} <Search></Search>
            </div>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#e5ecf8',
      height: 30
    }
  }));

  return (
    <>
      <Loader visible={loading} fullscreen={true} />

      <SearchSensors
        type={type}
        setSearchTerm={(e: any) => setSearchTerm(e)}
        searchTerm={searchTerm}
        setLoading={(e: boolean) => setLoading(e)}
        getSensors={(e: any) => getSensors(e)}
        setSensors={(e: any) => setSensors(e)}
        setFrom={(e: any) =>{console.log(e); setFrom(e);}}
        setTo={(e: any) => setTo(e)}
        from={from}
        to={to}
        setShowError={(e: boolean) => setShowError(e)}
        setErrorMessage={(e: string) => setErrorMessage(e)}
      ></SearchSensors>

      {pagedSensors.length > 0 ? (
        <>
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                  <StyledTableCell align="left">{t('SENSOR_ID')}</StyledTableCell>
                  <StyledTableCell align="left">{t('LAST_DATA_SENT')}</StyledTableCell>
                  <StyledTableCell align="left">{t('ACTIONS')}</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sensors.map((row) => (
                    <Row key={row.name} row={row} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={sensors.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      ) : (
        <Card className="cardResult">
          <InfoIcon
            fontSize="large"
            className="infoIcon"
            color="info"
          ></InfoIcon>
          <Typography variant="h5">
            {' '}
            {t('SEARCH_SENSOR_FOR_DETAILS')}
          </Typography>
        </Card>
      )}

      <Dialog className='dialog' fullWidth open={showHistory} onClose={handleClose}>
        <DialogTitle>{t('SENSOR_HISTORY')}</DialogTitle>
        <DialogContent>{renderTable(history, true)}</DialogContent>
      </Dialog>
      <CustomAlert
          type={CustomAlertType.ERROR}
          show={showError}
          message={errorMessage}
          onClose={() => {
            setShowError(false);
            setErrorMessage('');
          }}
        />
    </>
  );
};

export default SensorsTesting;
